import { useEffect, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import { Navigate, Outlet, Route, Routes, useOutletContext } from "react-router-dom";
import { fetchPageContent } from "./api.js";
import AnnouncementBar from "./components/AnnouncementBar.jsx";
import Footer from "./components/Footer.jsx";
import SiteNavbar from "./components/Navbar.jsx";
import { AuthProvider, useAuth } from "./context/AuthContext.jsx";
import AboutPage from "./pages/AboutPage.jsx";
import CatalogItemPage from "./pages/CatalogItemPage.jsx";
import CatalogPage from "./pages/CatalogPage.jsx";
import ContactsPage from "./pages/ContactsPage.jsx";
import ErrorPage from "./pages/ErrorPage.jsx";
import LandingPage from "./pages/LandingPage.jsx";
import LoginPage from "./pages/LoginPage.jsx";
import ProfilePage from "./pages/ProfilePage.jsx";
import SearchPage from "./pages/SearchPage.jsx";

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route element={<Shell />}>
          <Route index element={<LandingOutlet />} />
          <Route path="catalog" element={<CatalogPage />} />
          <Route path="catalog/:sku" element={<CatalogItemPage />} />
          <Route path="search" element={<SearchPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="contacts" element={<ContactsPage />} />
          <Route path="login" element={<GuestRoute><LoginPage /></GuestRoute>} />
          <Route path="profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="403" element={<ErrorPage code={403} />} />
          <Route path="*" element={<ErrorPage code={404} />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/403" replace />;
  return children;
}

function GuestRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/profile" replace /> : children;
}

function Shell() {
  const [page, setPage] = useState(null);
  const [pageError, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchPageContent("landing");
        if (!cancelled) {
          setPage(data);
          if (data?.title) document.title = data.title;
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load page content");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="d-flex flex-column min-vh-100">
      <AnnouncementBar
        message={page?.site?.announcement?.message}
        linkLabel={page?.site?.announcement?.linkLabel}
        linkHref={page?.site?.announcement?.linkHref}
      />
      <SiteNavbar site={page?.site} />
      <main className="flex-grow-1 d-flex flex-column">
        {loading ? (
          <div className="py-5 d-flex justify-content-center">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Loading</span>
            </Spinner>
          </div>
        ) : (
          <Outlet context={{ page, pageError }} />
        )}
      </main>
      <Footer site={page?.site} />
    </div>
  );
}

function LandingOutlet() {
  const { page, pageError } = useOutletContext();
  if (pageError) {
    return (
      <div className="py-3">
        <div className="container py-2">
          <Alert variant="danger">{pageError}</Alert>
        </div>
      </div>
    );
  }
  return <LandingPage page={page} />;
}
